/**
 * Admin Settings Page
 * System configuration and integrations management
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Settings, Camera, Mail, MessageSquare, 
  Shield, Clock, Users, Play, Loader2, CheckCircle,
  XCircle, RefreshCw, Save, AlertTriangle, Zap,
  Image, ToggleLeft, ToggleRight, Phone, MessageCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { Input } from '../../components/ui/input';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';

// Integration card component
const IntegrationCard = ({ 
  icon: Icon, 
  name, 
  description, 
  configured, 
  onTest, 
  testing,
  testResult 
}) => (
  <div className="glass rounded-xl p-4">
    <div className="flex items-start gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
        configured ? 'bg-green-500/20' : 'bg-yellow-500/20'
      }`}>
        <Icon className={`w-6 h-6 ${configured ? 'text-green-400' : 'text-yellow-400'}`} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-medium">{name}</h3>
          {configured ? (
            <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400">
              Configurado
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-400">
              No configurado
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
        
        {testResult && (
          <div className={`mt-2 text-xs p-2 rounded ${
            testResult.success ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
          }`}>
            {testResult.success ? (
              <span className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                {testResult.message}
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <XCircle className="w-3 h-3" />
                {testResult.error}
              </span>
            )}
          </div>
        )}
      </div>
      
      {configured && onTest && (
        <Button
          variant="outline"
          size="sm"
          onClick={onTest}
          disabled={testing}
        >
          {testing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            'Probar'
          )}
        </Button>
      )}
    </div>
  </div>
);

// Setting row component
const SettingRow = ({ icon: Icon, label, description, children }) => (
  <div className="flex items-center justify-between py-4 border-b border-border last:border-0">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
        <Icon className="w-5 h-5 text-muted-foreground" />
      </div>
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
    {children}
  </div>
);

export default function AdminSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState({});
  
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getSettings();
      setSettings(res.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
      toast.error('Error cargando configuración');
    } finally {
      setLoading(false);
    }
  };
  
  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };
  
  const saveSettings = async () => {
    try {
      setSaving(true);
      await adminAPI.updateSettings({
        clip_duration_normal: settings.clip_duration_normal,
        clip_duration_fast: settings.clip_duration_fast,
        max_users_per_court: settings.max_users_per_court,
        cooldown_seconds: settings.cooldown_seconds,
        maintenance_mode: settings.maintenance_mode,
        allow_registrations: settings.allow_registrations,
        require_phone_verification: settings.require_phone_verification,
        auto_delete_clips_days: settings.auto_delete_clips_days,
        max_clips_per_user_day: settings.max_clips_per_user_day,
        enable_watermarks: settings.enable_watermarks,
        enable_fast_replay: settings.enable_fast_replay,
        support_whatsapp: settings.support_whatsapp,
        support_whatsapp_message: settings.support_whatsapp_message
      });
      toast.success('Configuración guardada');
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };
  
  const testIntegration = async (integration) => {
    setTesting(prev => ({ ...prev, [integration]: true }));
    setTestResults(prev => ({ ...prev, [integration]: null }));
    
    try {
      let res;
      if (integration === 'hikconnect') {
        res = await adminAPI.testHikConnect();
      } else if (integration === 'resend') {
        res = await adminAPI.testResend();
      }
      setTestResults(prev => ({ ...prev, [integration]: res.data }));
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [integration]: { success: false, error: err.message }
      }));
    } finally {
      setTesting(prev => ({ ...prev, [integration]: false }));
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">Configuración</h1>
              <p className="text-xs text-muted-foreground">Ajustes del sistema</p>
            </div>
          </div>
          
          {hasChanges && (
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Guardar
            </Button>
          )}
        </div>
      </header>
      
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Integrations Section */}
        <section>
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Integraciones
          </h2>
          <div className="space-y-3">
            <IntegrationCard
              icon={Camera}
              name="Hik-Connect"
              description="Conexión con cámaras Hikvision para grabación de clips"
              configured={settings?.hikconnect_configured}
              onTest={() => testIntegration('hikconnect')}
              testing={testing.hikconnect}
              testResult={testResults.hikconnect}
            />
            <IntegrationCard
              icon={Mail}
              name="Resend"
              description="Servicio de email para recuperación de contraseña"
              configured={settings?.resend_configured}
              onTest={() => testIntegration('resend')}
              testing={testing.resend}
              testResult={testResults.resend}
            />
            <IntegrationCard
              icon={MessageSquare}
              name="Twilio"
              description="SMS para verificación de teléfono"
              configured={settings?.twilio_configured}
            />
          </div>
        </section>
        
        {/* Clips Settings */}
        <section className="glass rounded-xl p-4">
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <Play className="w-5 h-5 text-primary" />
            Configuración de Clips
          </h2>
          
          <SettingRow
            icon={Clock}
            label="Duración clip normal"
            description="Segundos de video para replay normal"
          >
            <div className="flex items-center gap-3">
              <Slider
                value={[settings?.clip_duration_normal || 30]}
                onValueChange={([v]) => updateSetting('clip_duration_normal', v)}
                min={15}
                max={60}
                step={5}
                className="w-32"
              />
              <span className="text-sm font-medium w-12">{settings?.clip_duration_normal}s</span>
            </div>
          </SettingRow>
          
          <SettingRow
            icon={Zap}
            label="Duración clip rápido"
            description="Segundos capturados para fast replay (se acelera 2x)"
          >
            <div className="flex items-center gap-3">
              <Slider
                value={[settings?.clip_duration_fast || 60]}
                onValueChange={([v]) => updateSetting('clip_duration_fast', v)}
                min={30}
                max={120}
                step={10}
                className="w-32"
              />
              <span className="text-sm font-medium w-12">{settings?.clip_duration_fast}s</span>
            </div>
          </SettingRow>
          
          <SettingRow
            icon={Clock}
            label="Cooldown entre clips"
            description="Segundos de espera entre solicitudes por usuario"
          >
            <div className="flex items-center gap-3">
              <Slider
                value={[settings?.cooldown_seconds || 15]}
                onValueChange={([v]) => updateSetting('cooldown_seconds', v)}
                min={5}
                max={60}
                step={5}
                className="w-32"
              />
              <span className="text-sm font-medium w-12">{settings?.cooldown_seconds}s</span>
            </div>
          </SettingRow>
          
          <SettingRow
            icon={Image}
            label="Watermarks habilitados"
            description="Aplicar logos a los videos generados"
          >
            <Switch
              checked={settings?.enable_watermarks}
              onCheckedChange={(v) => updateSetting('enable_watermarks', v)}
            />
          </SettingRow>
          
          <SettingRow
            icon={Play}
            label="Fast replay habilitado"
            description="Permitir modo de replay rápido (2x velocidad)"
          >
            <Switch
              checked={settings?.enable_fast_replay}
              onCheckedChange={(v) => updateSetting('enable_fast_replay', v)}
            />
          </SettingRow>
        </section>
        
        {/* User Settings */}
        <section className="glass rounded-xl p-4">
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Configuración de Usuarios
          </h2>
          
          <SettingRow
            icon={Users}
            label="Máximo usuarios por cancha"
            description="Límite de usuarios simultáneos en una cancha"
          >
            <div className="flex items-center gap-3">
              <Slider
                value={[settings?.max_users_per_court || 10]}
                onValueChange={([v]) => updateSetting('max_users_per_court', v)}
                min={1}
                max={20}
                step={1}
                className="w-32"
              />
              <span className="text-sm font-medium w-12">{settings?.max_users_per_court}</span>
            </div>
          </SettingRow>
          
          <SettingRow
            icon={Play}
            label="Máximo clips por usuario/día"
            description="Límite diario de clips para usuarios free"
          >
            <div className="flex items-center gap-3">
              <Slider
                value={[settings?.max_clips_per_user_day || 50]}
                onValueChange={([v]) => updateSetting('max_clips_per_user_day', v)}
                min={5}
                max={100}
                step={5}
                className="w-32"
              />
              <span className="text-sm font-medium w-12">{settings?.max_clips_per_user_day}</span>
            </div>
          </SettingRow>
          
          <SettingRow
            icon={Shield}
            label="Permitir registros"
            description="Permitir nuevos usuarios registrarse"
          >
            <Switch
              checked={settings?.allow_registrations}
              onCheckedChange={(v) => updateSetting('allow_registrations', v)}
            />
          </SettingRow>
          
          <SettingRow
            icon={MessageSquare}
            label="Verificación de teléfono"
            description="Requerir verificación SMS al registrarse"
          >
            <Switch
              checked={settings?.require_phone_verification}
              onCheckedChange={(v) => updateSetting('require_phone_verification', v)}
            />
          </SettingRow>
        </section>
        
        {/* Support Settings */}
        <section className="glass rounded-xl p-4">
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-green-500" />
            Soporte WhatsApp (Clubes)
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                Número de WhatsApp
              </label>
              <Input
                type="tel"
                placeholder="+521234567890"
                value={settings?.support_whatsapp || ''}
                onChange={(e) => updateSetting('support_whatsapp', e.target.value)}
                className="max-w-xs"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Formato internacional con código de país (ej: +521234567890)
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium block mb-2">
                <MessageSquare className="w-4 h-4 inline mr-2" />
                Mensaje predeterminado
              </label>
              <Input
                type="text"
                placeholder="Hola, necesito ayuda con mi club en Clipper"
                value={settings?.support_whatsapp_message || ''}
                onChange={(e) => updateSetting('support_whatsapp_message', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Mensaje que verán los clubes al contactar soporte
              </p>
            </div>
            
            {settings?.support_whatsapp && (
              <div className="bg-green-500/10 p-3 rounded-lg border border-green-500/20">
                <p className="text-sm text-green-400 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  WhatsApp configurado: {settings.support_whatsapp}
                </p>
              </div>
            )}
          </div>
        </section>
        
        {/* System Settings */}
        <section className="glass rounded-xl p-4">
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            Sistema
          </h2>
          
          <SettingRow
            icon={AlertTriangle}
            label="Modo mantenimiento"
            description="Desactiva temporalmente la app para usuarios"
          >
            <Switch
              checked={settings?.maintenance_mode}
              onCheckedChange={(v) => updateSetting('maintenance_mode', v)}
            />
          </SettingRow>
          
          <SettingRow
            icon={Clock}
            label="Auto-eliminar clips"
            description="Días antes de eliminar clips automáticamente"
          >
            <div className="flex items-center gap-3">
              <Slider
                value={[settings?.auto_delete_clips_days || 30]}
                onValueChange={([v]) => updateSetting('auto_delete_clips_days', v)}
                min={7}
                max={90}
                step={1}
                className="w-32"
              />
              <span className="text-sm font-medium w-16">{settings?.auto_delete_clips_days} días</span>
            </div>
          </SettingRow>
        </section>
        
        {/* Info */}
        <div className="text-center text-xs text-muted-foreground py-4">
          <p>Última actualización: {settings?.updated_at ? new Date(settings.updated_at).toLocaleString('es-MX') : 'Nunca'}</p>
          {settings?.updated_by && <p>Por: {settings.updated_by}</p>}
        </div>
      </div>
    </div>
  );
}
